import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '@services/error.service';
import { IdleService } from '@services/idle.service';
import { Subject, takeUntil } from 'rxjs';
import { AuthManagerService } from '@services/auth-manager.service';
import { StorageService } from '@services/storage.service';

/**
 * Root component of the application
 * Handles initialization, authentication, and global event listeners
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  /** Flag for 404 page */
  notFound = false;

  /** Loading state during initialization */
  loading = true;

  /** Subject for managing subscriptions */
  private destroy$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private errorService: ErrorService,
    private idleService: IdleService,
    private authManager: AuthManagerService,
    private storageService: StorageService,
  ) {}

  /**
   * Initialize the application
   * Sets up language, authentication, and error handling
   */
  ngOnInit(): void {
    this.initializeLanguage();
    this.initializeAuthentication();
    this.initializeErrorHandling();
  }

  /**
   * Clean up subscriptions when component is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize language settings
   * Sets default language if not already set
   */
  private initializeLanguage(): void {
    const defaultLang = 'en';
    let lang = this.storageService.getLanguage();

    if (!lang) {
      this.storageService.setLanguage(defaultLang);
      lang = defaultLang;
    }

    this.translateService.use(lang);
  }

  /**
   * Initialize authentication flow
   * Handles authenticated and unauthenticated states
   */
  private initializeAuthentication(): void {
    if (window.location.pathname.startsWith('/public')) {
      this.loading = false;
      return;
    }

    this.authManager
      .initializeAuth()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ authenticated }) => {
          // Delay hiding the loading indicator to prevent UI flicker
          setTimeout(() => {
            this.loading = false;
          }, 500);

          // Start monitoring for idle timeout
          if (authenticated) {
            this.idleService.startMonitoring();
          }
        },
        error: (error) => {
          console.error('Authentication initialization error:', error);
          this.loading = false;
        },
      });
  }

  /**
   * Initialize error handling
   * Sets up listeners for 404 errors
   */
  private initializeErrorHandling(): void {
    this.errorService.notFound
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        // Uncomment if 404 handling is needed
        // this.notFound = status;
      });
  }
}
